import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

// Safari (WebKit) and low-power mobile devices choke on per-frame SVG feGaussianBlur,
// offset-path animation, and pathLength drawing of very complex paths.
const isSafari = typeof navigator !== 'undefined' &&
  /^((?!chrome|crios|android).)*safari/i.test(navigator.userAgent);
const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
// Light mode: drop the expensive SVG glow filter + animated pen circles (keeps the
// line-drawing animation, removes the per-frame filter/offset-path cost).
const lightMode = isSafari || isMobile;
// Keep the drawing animation on all devices (user wants it). We preserve it and
// instead remove the heavy glow/pen effects above.
const skipDraw = false;

const LogoDrawing = ({ onComplete }) => {
  const [animationKey, setAnimationKey] = useState(0);
  const [fadeOut, setFadeOut] = useState(false);
  
  // Separated paths from the user's Adobe Illustrator SVG
  const logoPaths = [
    "M405.05,510.74c1.89-1.12,3.32-2.35,3.84-4.1l10.89-36.08,7.39-24.84,6.16-20.75,6.95-23.81,12.01-41.2,11.6-39.36,8.3-28.75,4.28-14.53,6.31-20.99c.4-1.32.48-3.85.35-5.22l-24.27-.72-18.32,62.39-7.46,26.34-4.69,16.52-8.36,28.64-4.17,13.98-8.76,30.1-9.59,31.48-8.83,29.3-6.71,21c-1.8,5.64-3.07,9.96-4.58,16.26,3.14-1.13,4.48-1.88,6.66-3.18l21-12.47Z",
    "M507.05,343.28l9.66-31.67,5.75-18.36c.81,1.28,1.75,2.65,2.2,3.86l23.09,61.98,9.82,27.02,7.2,19.73,8.34,22.59,31.79-10.16,20.45-6.7-15.25-39.38-6.21-16.05-10.02-25.81-9.78-25.21-4.51-11.34-16.88-43.02-44.32-.14-12.15,37.92-8.17,26.83-6,18.12-3.3,10.54-7.38,24.56-6.17,19.86-17.63,56.32-11.42,35.75c-.84,2.62-1.24,4.8-1.17,7.84l20.19-11.13,6.02-19.21,7.87-25.08,10.32-32.94,3.85-11.99,4.8-15.27,9-29.5Z",
    "M714.01,400.76l-25.08,3.87c-16.54,2.55-51.87,11.41-68.18,16.8l-24.06,7.96c-19.78,6.55-66.18,25.3-84.54,34.42l-20.77,10.32-7.9,4.05-13.11,6.94-17.38,9.73-11.36,6.61-27.97,16.95-16.79,10.37-18.25,11.66-12.44,7.64c-11.71,7.19-24.43,12.34-38.13,14.75-9.8,1.73-19.09.55-28.27-2.89-5.57-2.09-11.82-7.6-15.27-12.4-6.07-8.45-9.88-21.94-10.57-32.41-2.06-31.22,11.06-68.49,32.91-91.21,8.31-8.64,17.55-15.33,28.02-20.9,14.2-7.55,29.88-11.08,46.13-9.85,15.87,1.2,17.22,3.93,31.82,7.05,2.23-4.39-6.02-7.32-8.27-7.94l-14.17-3.89c-14.2-3.9-36.32-3.51-50.98-.08-19.85,4.64-29.38,8.70-46.16,19.73-13.09,8.61-23.94,19.28-33.72,31.51-5.43,6.79-9.55,14.16-13.28,22.02-8.93,18.78-14.35,37.86-13,58.81,1.15,17.92,4.53,27.04,13.4,41.86,2.89,4.83,9.87,11.10,14.56,14.77,13.63,10.66,31.59,13.44,48.62,11.86,27.88-2.59,53.01-15.75,76.38-30.42l25.79-16.19,13.62-8.58,31.2-18.74,9.38-5.55,31.73-17.28,19.97-9.96,31.43-14.61,30.29-12.73,51.33-19.72,24.16-8.70,15.63-5.44c17.18-5.98,36.93-10.99,54.89-14.20,9.92-1.77,33.98-5.19,43.34-5.55l14.19-.54-6.50-1.43c-1.53-.34-4.56-.26-6.65-.34-14.37-.54-42.30-.23-55.97,1.88Z",
    "M500.97,458.97l18.69-9.86-34.83-.79c-1.41.28-4.21,1.78-4.60,3.04l-2.23,7.32c1.83.29,3.70.64,4.89.62l18.09-.33Z",
    "M388.46,588.87l35.57.14c2.80.01,4.43-1.45,5.24-4.03l9.12-28.83c1.29-4.07,2.20-7.82,3.43-12.73-13.94,1.57-9.50.74-19.84,9.37l-17.47,14.58-7.15,5.99-9.78,8.21c-.86.72-1.77,2.79-1.93,3.78s1.81,3.51,2.81,3.51Z",
    "M639.93,452.21c-2.21,1.23-4.67,2.33-7.36,3.40l-16.57,6.61-9.19,5.09-6.08,2.44c-1.12.45-3.21,2.05-4.09,2.74l-3.05-1.32c13.16,17.16,33.73,28.70,53.70,35.82l15.46,5.51,15.21,4.82,33.14,9.90c7.76,2.32,14.86,5.51,22.34,8.61,5.03,2.09,9.67,5.37,13.80,9.03,8.87,7.86,7.88,22.73.21,29.65-4.72,4.26-10.73,7.89-17.06,9.48-9.12,2.29-18.90,3.79-28.18,2.63-19.10-2.39-39-8.98-54.23-20.71-13.34-10.28-24.27-22.92-31.75-37.98l-9.33-18.80c-.94-1.89-3.51-3.66-6.01-2.82-.54.18-.60,1.72-.38,2.32l7.28,20.30,20.03,55.78c.36,1.01,2.12,3.90,3.59,3.90h65.45s6.15.16,6.15.16l18.03.35c22.45.44,52.88-6.74,63.62-28.70,7.02-14.34,4.87-30.59-5.68-42.47-5.51-6.20-11.61-11.28-19-15.22-8.49-4.53-16.83-8.89-25.99-11.99l-40.27-13.65-8.14-2.76c-14.94-5.07-28.11-10.81-40.81-20.16-1.16-.85-2.26-1.56-3.72-2.40l-1.91-2.03.78,2.44Z"
  ];

  // Timing configuration for each stroke
  const strokeDurations = [0.8, 1.2, 1.8, 0.4, 0.6, 1.2];
  const getStrokeDelay = (index) => {
    let delay = 0;
    for (let i = 0; i < index; i++) {
      delay += strokeDurations[i];
    }
    return delay;
  };

  const totalDrawDuration = skipDraw ? 1.0 : strokeDurations.reduce((a, b) => a + b, 0);

  useEffect(() => {
    // After all strokes are drawn, trigger fade out
    const fadeTimer = setTimeout(() => {
      setFadeOut(true);
    }, (totalDrawDuration + 0.3) * 1000);

    // Callback when animation is complete
    const completeTimer = setTimeout(() => {
      if (onComplete) onComplete();
    }, (totalDrawDuration + 2.8) * 1000);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete, animationKey, totalDrawDuration]);

  return (
    <div className="absolute inset-0 w-full h-full flex items-center justify-center overflow-hidden md:translate-y-0 -translate-y-[16%]">
      <motion.svg
        key={animationKey}
        viewBox="250 200 520 500"
        className="w-[55vw] md:w-[42vw] h-[35vh] md:h-[95vh]"
        style={{ 
          overflow: 'visible',
          maxWidth: '800px',
          maxHeight: '950px'
        }}
        preserveAspectRatio="xMidYMid meet"
        animate={{
          opacity: fadeOut ? 0.12 : 1
        }}
        transition={{
          opacity: { duration: 2.5, ease: "easeInOut" }
        }}
      >
        <defs>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <filter id="penGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="8" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Render each path with staggered animation */}
        {logoPaths.map((path, index) => (
          <motion.path
            key={index}
            d={path}
            fill="none"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            {...(lightMode ? {} : { filter: 'url(#glow)' })}
            initial={{ pathLength: skipDraw ? 1 : 0, opacity: 0 }}
            animate={{ 
              pathLength: 1, 
              opacity: 1,
              stroke: fadeOut ? "#C9A227" : "#ffffff"
            }}
            transition={{
              pathLength: skipDraw ? { duration: 0 } : { 
                duration: strokeDurations[index], 
                delay: getStrokeDelay(index),
                ease: "easeInOut"
              },
              opacity: skipDraw ? {
                duration: 0.5,
                delay: index * 0.08
              } : {
                duration: 0.1,
                delay: getStrokeDelay(index)
              },
              stroke: {
                duration: 1,
                ease: "easeInOut"
              }
            }}
          />
        ))}

        {/* Animated pen cursor - skipped on Safari/mobile (offset-path + blur too heavy) */}
        {!lightMode && logoPaths.map((path, index) => (
          <motion.circle
            key={`pen-${index}`}
            r="5"
            fill="#FFD700"
            filter="url(#penGlow)"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: fadeOut ? 0 : [0, 1, 1, 0],
              offsetDistance: ["0%", "100%"]
            }}
            transition={{
              opacity: {
                duration: strokeDurations[index],
                delay: getStrokeDelay(index),
                times: [0, 0.02, 0.95, 1]
              },
              offsetDistance: {
                duration: strokeDurations[index],
                delay: getStrokeDelay(index),
                ease: "easeInOut"
              }
            }}
            style={{
              offsetPath: `path("${path}")`,
            }}
          />
        ))}
      </motion.svg>
    </div>
  );
};

export default LogoDrawing;
